import clsx from 'clsx'
import {Nav, Navbar, NavDropdown} from 'react-bootstrap'
import {Link, useLocation} from 'react-router-dom'
import {useAuth} from '../../../../app/modules/auth'
import {FEATURES, ROUTES} from '../../../constants'
import {isFeatureEnabled, KTSVG} from '../../../helpers'
import UseRoutesPermission from '../../../hooks/UseRoutesPermission'
import {useIntl} from 'react-intl'
import {
  isActiveEnterpriseManager,
  isEnterpriseOwner,
} from '../../../../app/modules/auth/core/userHelpers'

type HeaderNavItem = {
  label: string
  to?: string
  isVisible: boolean
  isActive?: boolean
  children?: HeaderNavLinkItem[]
}

type HeaderNavLinkItem = HeaderNavItem & {
  to: string
  children?: never
}

const isPathActive = (pathname: string, targets: string | string[]) => {
  const values = Array.isArray(targets) ? targets : [targets]

  return values.some((target) => pathname === target || pathname.startsWith(`${target}/`))
}

const getLinkClassName = (isActive?: boolean) => clsx('header-navbar__link', {active: isActive})

const hasLink = (item: HeaderNavItem): item is HeaderNavLinkItem => Boolean(item.to)

export const HeaderNavigation = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {routes} = UseRoutesPermission()
  const {pathname} = useLocation()

  if (!currentUser) {
    return null
  }

  const hasDashboardAccess = routes.DASHBOARD.hasPermission && isFeatureEnabled(FEATURES.DASHBOARD)
  const canAccessOrganizations =
    isFeatureEnabled(FEATURES.ORGANIZATIONS) &&
    (isActiveEnterpriseManager(currentUser) || isEnterpriseOwner(currentUser))

  const navItems: HeaderNavItem[] = [
    {
      label: intl.formatMessage({id: 'ORGANIZATION'}),
      to: ROUTES.ORGANIZATIONS.BASE,
      isVisible: canAccessOrganizations,
      isActive: isPathActive(pathname, ROUTES.ORGANIZATIONS.BASE),
    },
    {
      label: intl.formatMessage({id: 'ORDERS'}),
      to: ROUTES.ORDERS.BASE,
      isVisible: routes.ORDERS_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.ORDERS.BASE),
    },
    {
      label: intl.formatMessage({id: 'BATCHES'}),
      to: ROUTES.BATCHES.BASE,
      isVisible: routes.BATCHES_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.BATCHES.BASE),
    },
    {
      label: intl.formatMessage({id: 'SHIPMENTS'}),
      to: ROUTES.SHIPMENTS.BASE,
      isVisible: routes.SHIPMENTS_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.SHIPMENTS.BASE),
    },
    {
      label: intl.formatMessage({id: 'END_OF_DAY'}),
      to: ROUTES.END_OF_DAY.OPEN_SHIPMENTS,
      isVisible: routes.SHIPMENTS_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.END_OF_DAY.BASE),
    },
    {
      label: intl.formatMessage({id: 'CLAIMS'}),
      to: ROUTES.CLAIMS.BASE,
      isVisible: routes.CLAIMS_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.CLAIMS.BASE),
    },
    {
      label: intl.formatMessage({id: 'REPORTS'}),
      to: ROUTES.REPORTS.BASE,
      isVisible: true,
      isActive: isPathActive(pathname, ROUTES.REPORTS.BASE),
    },
    {
      label: intl.formatMessage({id: 'ANALYTICS'}),
      to: ROUTES.DASHBOARD.BASE,
      isVisible: hasDashboardAccess,
      isActive: isPathActive(pathname, ROUTES.DASHBOARD.BASE),
    },
    {
      label: 'A1',
      to: ROUTES.A1.BASE,
      isVisible: routes.BATCHES_VIEW.hasPermission,
      isActive: isPathActive(pathname, ROUTES.A1.BASE),
    },
    {
      label: intl.formatMessage({id: 'ADMIN'}),
      to: '/admin',
      isVisible: routes.SYSTEM_ADMIN.hasPermission,
      isActive: isPathActive(pathname, '/admin'),
    },
  ].filter((item) => item.isVisible)

  const compactOnlyNavItems: HeaderNavLinkItem[] = [
    {
      label: intl.formatMessage({id: 'SETTINGS'}),
      to: ROUTES.SETTINGS.PROFILE,
      isVisible: true,
      isActive: isPathActive(pathname, ROUTES.SETTINGS.BASE),
    },
  ].filter((item) => item.isVisible)

  const compactNavItems: HeaderNavLinkItem[] = [
    ...navItems.flatMap((item) => {
      if (item.children?.length) {
        return item.children.map((child) => ({
          ...child,
          isActive: isPathActive(pathname, child.to),
        }))
      }

      return hasLink(item) ? [item] : []
    }),
    ...compactOnlyNavItems,
  ].filter(hasLink)

  const activeCompactItem = compactNavItems.find((item) => item.isActive)
  const compactDropdownTitle =
    activeCompactItem?.label ?? compactNavItems[0]?.label ?? intl.formatMessage({id: 'ORDERS'})

  return (
    <div className='header-navbar'>
      <Navbar className='p-0'>
        <Nav className='align-items-center flex-nowrap header-navbar__desktop'>
          {navItems.map((item) =>
            item.children?.length ? (
              <NavDropdown
                title={
                  <span className='header-navbar__dropdown-title'>
                    <span>{item.label}</span>
                    <KTSVG
                      path='/media/gori/arrows/arrow_down.svg'
                      className='header-navbar__dropdown-icon'
                      svgClassName=''
                      small={false}
                    />
                  </span>
                }
                id='gori-header-orders-dropdown'
                key={item.label}
                className={clsx('header-navbar__dropdown', {
                  'header-navbar__dropdown--active': item.isActive,
                })}
                renderMenuOnMount
              >
                {item.children
                  .filter((child) => !isPathActive(pathname, child.to))
                  .map((child) => (
                    <NavDropdown.Item
                      as={Link}
                      to={child.to}
                      key={child.label}
                      style={{color: '#0B6D65'}}
                    >
                      {child.label}
                    </NavDropdown.Item>
                  ))}
              </NavDropdown>
            ) : hasLink(item) ? (
              <Nav.Link
                as={Link}
                to={item.to}
                key={item.label}
                className={getLinkClassName(item.isActive)}
              >
                {item.label}
              </Nav.Link>
            ) : null
          )}
        </Nav>

        <Nav className='align-items-center header-navbar__compact'>
          <NavDropdown
            title={
              <span className='header-navbar__dropdown-title'>
                <span>{compactDropdownTitle}</span>
                <KTSVG
                  path='/media/gori/arrows/arrow_down.svg'
                  className='header-navbar__dropdown-icon'
                  svgClassName=''
                  small={false}
                />
              </span>
            }
            id='gori-header-compact-dropdown'
            className={clsx(
              'header-navbar__dropdown',
              'header-navbar__dropdown--compact',
              activeCompactItem
                ? 'header-navbar__dropdown--active'
                : 'header-navbar__dropdown--inactive'
            )}
            renderMenuOnMount
          >
            {compactNavItems.map((item) => (
              <NavDropdown.Item
                as={Link}
                to={item.to}
                key={item.label}
                className={clsx({active: item.isActive})}
              >
                {item.label}
              </NavDropdown.Item>
            ))}
          </NavDropdown>
        </Nav>
      </Navbar>
    </div>
  )
}
